import { TDeviceType } from '@cian/ui-kit';

import { getNextPageNumber } from './utils';
import { getTermValue, setTerm, TJsonQuery } from '../../../../packages/JsonQuery';
import { IPublicWebV1SearchResponse } from '../../repositories/dailyrent/entities/response/PublicWebV1SearchResponse';
import { selectIsDesktopListingAvailable } from '../../selectors/desktopListing';
import { selectShouldLoadListingForHybridSearch } from '../../selectors/hybridSearch';
import { TThunkAction } from '../../types/redux';
import { getJsonQueryWithoutRequestOnlyTerms } from '../../utils/jsonQuery/getJsonQueryWithoutRequestOnlyTerms';
import { scrollPageToTop } from '../../utils/scrollPageToTop';
import { fetchMapData, IFetchMapDataParameters } from '../fetchMapData/fetchMapData';
import { IFetchMapDataSucceedParameters } from '../fetchMapData/types';
import { justChangeJsonQuery } from '../filters/justChangeJsonQuery';
import {
  failureListingOffers,
  loadListingOffersByIds,
  markListingEmpty,
  requestListingOffers,
  setPage,
  successListingOffers,
  TLoadListingOffersMode,
} from '../listingOffers';
import {
  isActualListingOffersByIdsRequest,
  startListingOffersByIdsRequest,
  TListingOffersByIdsRequestId,
} from '../listingOffers/listingOffersByIdsRequestGuard';

interface IFetchHybridSearchDataParameters extends Omit<IFetchMapDataParameters, 'requestJsonQuery'> {
  mode?: TLoadListingOffersMode;
  deviceType: TDeviceType;
  shouldLoadListingOverride?: boolean;
}

export interface IFetchHybridSearchDataResult {
  mapData: IFetchMapDataSucceedParameters;
  listingOffers?: IPublicWebV1SearchResponse;
}

function getSortedOfferIds(mapData: IFetchMapDataSucceedParameters): number[] {
  if (!Array.isArray(mapData.sortedOfferIds)) {
    throw new Error('fetchHybridSearchData: expected sortedOfferIds in map response');
  }

  return mapData.sortedOfferIds;
}

export const fetchHybridSearchData =
  ({
    mode = 'reset',
    shouldLoadListingOverride,
    ...mapParameters
  }: IFetchHybridSearchDataParameters): TThunkAction<Promise<IFetchHybridSearchDataResult | undefined>> =>
  async (dispatch, getState) => {
    const state = getState();
    const {
      queryString,
      filters: { jsonQuery },
      listingOffers: { page: currentPage = 1 },
    } = state;
    const nextPageNumber = getNextPageNumber(mode, currentPage);
    const listingBaseJsonQuery = getJsonQueryWithoutRequestOnlyTerms(jsonQuery as TJsonQuery);
    const mapRequestJsonQuery = setTerm('page')(listingBaseJsonQuery)(nextPageNumber);
    const shouldLoadListing =
      shouldLoadListingOverride ??
      selectShouldLoadListingForHybridSearch(getState(), mapParameters.deviceType as TDeviceType);

    let listingRequestId: TListingOffersByIdsRequestId | null = null;
    if (shouldLoadListing) {
      // На desktop hybrid вся страница (со sticky-картой) скроллится через documentElement.
      // Возвращаем её наверх ДО сброса offers, иначе схлопнувшийся листинг обнажает футер.
      // Гард по селектору, а не deviceType: пагинация и мобильные пути deviceType не передают.
      if (selectIsDesktopListingAvailable(getState())) {
        scrollPageToTop();
      }

      listingRequestId = startListingOffersByIdsRequest();
      dispatch(requestListingOffers({ reset: mode === 'reset' }));
    }

    // shouldLoadListing && — страхует от ложного stale при shouldLoadListing=false
    // (тогда listingRequestId=null, а листинг и так не грузим).
    const isStaleListingRequest = () =>
      shouldLoadListing && listingRequestId !== null && !isActualListingOffersByIdsRequest(listingRequestId);

    const mapData = await dispatch(
      fetchMapData({
        ...mapParameters,
        requestJsonQuery: mapRequestJsonQuery,
      }),
    );

    // fetchMapData уже обработал skip/cancel/error: залогировал и обновил нужные статусы.
    if (!mapData) {
      if (shouldLoadListing && !isStaleListingRequest()) {
        dispatch(failureListingOffers());
      }

      return undefined;
    }

    if (!shouldLoadListing) {
      return { mapData };
    }

    // За время await fetchMapData мог стартовать более новый запрос листинга —
    // устаревший не трогает листинг и не делает лишний запрос офферов.
    if (isStaleListingRequest()) {
      return undefined;
    }

    let sortedOfferIds: number[];
    try {
      sortedOfferIds = getSortedOfferIds(mapData);
    } catch (error) {
      if (!isStaleListingRequest()) {
        dispatch(failureListingOffers());
      }

      throw error;
    }

    if (sortedOfferIds.length === 0) {
      dispatch(markListingEmpty());

      return { mapData };
    }

    try {
      const listingOffers = await dispatch(
        loadListingOffersByIds({
          baseJsonQuery: listingBaseJsonQuery,
          offerIds: sortedOfferIds,
          queryString,
          responseJsonQuery: mapData.jsonQuery as TJsonQuery,
          mapPageOffersCount: mapData.offersCount,
        }),
      );

      // Свежий запрос мог стартовать за время загрузки офферов — устаревший
      // не перетирает листинг свежего своими (уже неактуальными) офферами.
      if (isStaleListingRequest()) {
        return undefined;
      }

      const responsePage = getTermValue('page')(mapData.jsonQuery as TJsonQuery) || 1;

      dispatch(successListingOffers(listingOffers));
      dispatch(justChangeJsonQuery({ nextJsonQuery: mapData.jsonQuery as TJsonQuery }));
      dispatch(setPage(responsePage));

      return { mapData, listingOffers };
    } catch (error) {
      if (!isStaleListingRequest()) {
        dispatch(failureListingOffers());
      }

      throw error;
    }
  };
